"use client";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useMemo } from "react";
// eslint-disable-next-line import/no-extraneous-dependencies
import { useDropzone } from "react-dropzone";
import { forwardRef, useMultiStyleConfig, Box, useBreakpointValue, Input, } from "@chakra-ui/react";
import File from "./File";
import { Button, ButtonVariant } from "../../atoms/Button";
import { FieldTitle, SvgSpriteConsumer } from "../../atoms";
import { SIZE, VARIANT } from "./FileUploader.types";
import { Description } from "./FileUploader.helpers";
const FileUploader = forwardRef(({ id = "fileUploader", files = [], buttonText = "Upload a file", rightIcon, description = "or drag a file", activeDescription = "Drop the files here...", isLoading = false, accept = {}, capture, variant, helpText, maxFiles = 1, label, mandatory = false, addBorder = false, onFileUpload, onFileDelete, onFileDrop, onDragActive, onDropRejected, }, ref) => {
    const fileInputRef = useRef(null);
    const size = useBreakpointValue({
        sm: SIZE.MOBILE,
        md: SIZE.DESKTOP,
    }, {
        fallback: "md",
    });
    const styles = useMultiStyleConfig("FileUploader", {
        isDisabled: variant === VARIANT.DISABLED,
        isInvalid: variant === VARIANT.ERROR,
        addBorder,
        size,
        isMoreThanOneFile: files.length > 1,
    });
    const handleOnFileUpload = (newFiles) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        yield onFileUpload(newFiles);
        if ((_a = fileInputRef.current) === null || _a === void 0 ? void 0 : _a.value) {
            fileInputRef.current.value = "";
        }
    });
    const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
        onDrop: handleOnFileUpload,
        maxFiles,
        multiple: maxFiles > 1,
        accept,
        noClick: true,
        noDrag: size === SIZE.MOBILE,
        disabled: variant === VARIANT.DISABLED,
        onDropRejected,
    });
    useEffect(() => {
        if (onFileDrop) {
            onFileDrop(isDragReject);
        }
    }, [isDragReject, onFileDrop]);
    useEffect(() => {
        if (onDragActive) {
            onDragActive(isDragActive);
        }
    }, [isDragActive, onDragActive]);
    const FilesComponent = useMemo(() => (_jsx(Box, Object.assign({ sx: styles.files }, { children: files.map((filename, index) => (_jsx(File, { filename: filename, onClose: onFileDelete }, index))) }))), [files, onFileDelete, styles.files]);
    const FileInput = useMemo(() => (_jsxs(_Fragment, { children: [_jsx(Button, Object.assign({ size: "md", width: "7.25rem", variant: ButtonVariant.SECONDARY_FILLED, isLoading: isLoading && !variant, isDisabled: variant === VARIANT.DISABLED, onClick: (e) => {
                    var _a;
                    e.stopPropagation();
                    (_a = fileInputRef.current) === null || _a === void 0 ? void 0 : _a.click();
                } }, { children: buttonText })), _jsx(Input, Object.assign({}, getInputProps(), { id: id, type: "file", ref: fileInputRef, size: "md", capture: capture, accept: Object.keys(accept).join(",") }))] })), [accept, buttonText, capture, getInputProps, id, isLoading, variant]);
    return (_jsxs(Box, Object.assign({ ref: ref, sx: styles.fileUploader }, { children: [label && (_jsx(FieldTitle, { title: label, inputId: id, mandatory: mandatory })), size === SIZE.DESKTOP && (_jsxs(Box, Object.assign({}, getRootProps(), { className: `dropzone ${isDragActive ? "active" : ""}`, sx: styles.dropzone }, { children: [_jsxs(Box, Object.assign({ sx: styles.wrapper }, { children: [FileInput, _jsx(Box, Object.assign({ sx: styles.dropzoneFiles }, { children: files.length > 0
                                    ? FilesComponent
                                    : Description({
                                        size,
                                        isDragActive,
                                        description,
                                        activeDescription,
                                    }) }))] })), rightIcon && (_jsx(Box, Object.assign({ sx: styles.rightIcon }, { children: _jsx(SvgSpriteConsumer, { svgId: rightIcon, size: 24 }) })))] }))), size === SIZE.MOBILE && (_jsxs(Box, Object.assign({}, getRootProps(), { className: `dropzone ${isDragActive ? "active" : ""}`, sx: styles.dropzone }, { children: [_jsxs(Box, Object.assign({ sx: styles.wrapper }, { children: [FileInput, rightIcon && (_jsx(Box, Object.assign({ sx: styles.rightIcon }, { children: _jsx(SvgSpriteConsumer, { svgId: rightIcon, size: 24 }) })))] })), _jsx(Box, Object.assign({ sx: styles.dropzoneFiles }, { children: FilesComponent }))] }))), _jsx(Box, Object.assign({ sx: styles.helpText }, { children: helpText }))] })));
});
FileUploader.displayName = "FileUploader";
export default FileUploader;
//# sourceMappingURL=FileUploader.js.map